import {
	Box,
	Button,
	type ButtonOwnProps,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type SxProps,
	type Theme,
	Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { spaces } from '../../../assets/styles/themes/tokens';

interface OwnProps {
	title: string;
	message: string;
	isOpen: boolean;
	confirmButtonColor?: ButtonOwnProps['color'];
	onCloseModal: () => void;
	onConfirm: () => void;
	confirmButtonText?: string;
	cancelButtonText?: string;
	dialogSx?: SxProps<Theme>;
	confirmButtonSx?: SxProps<Theme>;
	isConfirmLoading?: boolean;
}

const ConfirmationDialog: React.FC<OwnProps> = (props) => {
	const { t } = useTranslation();
	const {
		title,
		message,
		isOpen,
		onCloseModal,
		onConfirm,
		confirmButtonText,
		confirmButtonColor = 'primary',
		cancelButtonText,
		dialogSx,
		confirmButtonSx,
		isConfirmLoading = false,
	} = props;

	return (
		<Dialog
			open={isOpen}
			onClose={isConfirmLoading ? undefined : onCloseModal}
			sx={{
				'& .MuiDialog-paper': {
					width: '100%',
				},
				...dialogSx,
			}}
		>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						padding: spaces.lg,
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.xs,
					}}
				>
					<Typography variant="h5" color="text.primary">
						{title}
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						{message}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ padding: spaces.lg }}>
				<Button
					onClick={onCloseModal}
					variant="text"
					color="secondary"
					size="medium"
					disabled={isConfirmLoading}
					data-testid="confirmation-dialog-cancel-button"
				>
					{cancelButtonText || t('Cancel')}
				</Button>
				<Button
					onClick={onConfirm}
					variant="contained"
					color={confirmButtonColor}
					size="medium"
					disabled={isConfirmLoading}
					startIcon={isConfirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
					sx={confirmButtonSx}
					data-testid="confirmation-dialog-confirm-button"
				>
					{confirmButtonText || t('Confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConfirmationDialog;
